/**
 * A country's name in the visitor's language (2026-09-30).
 *
 * /ru/prices, /zh/prices and every other non-English page listed all 193
 * countries in English — and so did the home ticker, the price teaser and the
 * rotating hero rate — while the server-rendered table they replace was
 * localized. The search already matched localized names through
 * Intl.DisplayNames; this makes the visible text use the same source.
 *
 * English keeps the curated catalog name ("Côte d'Ivoire", "Hong Kong SAR"
 * decisions live there); other locales use the browser's CLDR region names,
 * falling back to the catalog name when a code is missing or unknown.
 */
import { getLocale } from '../i18n';
import { INTL_LOCALE } from '../i18n/types';

/**
 * The names the server prints (data/country-names.json), loaded as their own
 * chunk at boot (2026-09-30). The browser's CLDR table is not the server's:
 * live Chrome said «Палестина» where the server printed «Палестинские
 * территории», so /ru/prices swapped a name on hydration although every test
 * (run on Node's ICU) passed. With the file loaded both sides read one source.
 * Until it arrives, the overrides below and Intl stand in.
 */
let NAMES: Record<string, Partial<Record<string, string>>> | null = null;
let namesLoading: Promise<void> | null = null;

export function ensureCountryNames(): Promise<void> {
  if (NAMES) return Promise.resolve();
  namesLoading ??= import('../../../data/country-names.json').then((m) => {
    NAMES = m.default as Record<string, Partial<Record<string, string>>>;
  });
  return namesLoading;
}

const cache = new Map<string, Intl.DisplayNames | null>();

function displayNames(tag: string): Intl.DisplayNames | null {
  if (cache.has(tag)) return cache.get(tag) ?? null;
  let dn: Intl.DisplayNames | null = null;
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      dn = new Intl.DisplayNames([tag], { type: 'region' });
    }
  } catch { dn = null; }
  cache.set(tag, dn);
  return dn;
}

/**
 * Where the server's curated names (data/country-names.json, what SSR prints)
 * differ from CLDR. zh keeps 中国台湾 / 中国香港 / 中国澳门, 巴勒斯坦地区 and
 * the shorter common forms. The other languages replace CLDR's reference-book
 * forms (2026-09-30): "Конго - Киншаса", "RAE de Hong Kong (China)",
 * "о-ва Кайман"; and GP names all three territories, as en and zh do. A test
 * holds this function equal to that file for every country and locale, so the
 * page does not change its country name when the client takes over.
 */
const CURATED: Partial<Record<string, Record<string, string>>> = {
  zh: {
    AE: '阿联酋', CD: '刚果民主共和国', CG: '刚果共和国', GP: '瓜德罗普/马提尼克/法属圭亚那',
    HK: '中国香港', MO: '中国澳门', PS: '巴勒斯坦地区', SZ: '埃斯瓦蒂尼', TW: '中国台湾',
  },
  ru: {
    BM: 'Бермуды', CD: 'ДР Конго', CG: 'Республика Конго', FO: 'Фарерские острова',
    GP: 'Гваделупа / Мартиника / Французская Гвиана',
    HK: 'Гонконг', KY: 'Каймановы острова', MO: 'Макао', SC: 'Сейшелы', VG: 'Британские Виргинские острова',
  },
  es: { GP: 'Guadalupe / Martinica / Guayana Francesa', HK: 'Hong Kong', MO: 'Macao' },
  pt: { CD: 'RD Congo', GP: 'Guadalupe / Martinica / Guiana Francesa', HK: 'Hong Kong', MO: 'Macau' },
  tr: {
    CD: 'DR Kongo', CG: 'Kongo Cumhuriyeti',
    GP: 'Guadeloupe / Martinik / Fransız Guyanası', HK: 'Hong Kong', MO: 'Makao',
  },
};

export function countryDisplayName(c: { slug?: string; name: string; isoAlpha2?: string | null }): string {
  const locale = getLocale();
  const printed = c.slug ? NAMES?.[c.slug]?.[locale] : undefined;
  if (printed) return printed;
  if (locale === 'en' || !c.isoAlpha2) return c.name;
  const curated = CURATED[locale]?.[c.isoAlpha2.toUpperCase()];
  if (curated) return curated;
  const dn = displayNames(INTL_LOCALE[locale] ?? locale);
  if (!dn) return c.name;
  try {
    const n = dn.of(c.isoAlpha2.toUpperCase());
    return n && n !== c.isoAlpha2.toUpperCase() ? n : c.name;
  } catch {
    return c.name;
  }
}

/** Collator for sorting by the displayed name in the active language. */
export function compareCountryNames(a: { slug?: string; name: string; isoAlpha2?: string | null }, b: { slug?: string; name: string; isoAlpha2?: string | null }): number {
  const tag = INTL_LOCALE[getLocale()] ?? 'en';
  return countryDisplayName(a).localeCompare(countryDisplayName(b), tag);
}
